import React, { useEffect } from "react";

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  icon?: string;
  width?: number;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

// Right-hand panel that opens over the current page, so the list underneath
// stays where it was while the user filters, creates or sends something.
const Drawer: React.FC<DrawerProps> = ({ isOpen, onClose, title, icon, width = 400, footer, children }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="position-fixed top-0 end-0 bottom-0 start-0 bg-dark bg-opacity-50"
        onClick={onClose}
        style={{ zIndex: 1040 }}
      />
      <div
        className="position-fixed top-0 end-0 bottom-0 bg-white shadow overflow-auto"
        style={{ width: `min(${width}px, 100vw)`, zIndex: 1045 }}
        role="dialog"
        aria-modal="true"
      >
        <div className="d-flex flex-column h-100">
          <div className="p-3 border-bottom bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-primary">
                {icon && <i className={`bi bi-${icon} me-2`}></i>}
                {title}
              </h5>
              <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
            </div>
          </div>
          <div className="p-3 flex-grow-1">{children}</div>
          {footer && <div className="p-3 border-top bg-light">{footer}</div>}
        </div>
      </div>
    </>
  );
};

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onClear: () => void;
  onApply: () => void;
  title: string;
  width?: number;
  children: React.ReactNode;
}

export const FilterDrawer: React.FC<FilterDrawerProps> = ({ isOpen, onClose, onClear, onApply, title, width, children }) => (
  <Drawer
    isOpen={isOpen}
    onClose={onClose}
    title={title}
    icon="funnel-fill"
    width={width}
    footer={
      <div className="d-grid gap-2">
        <button className="btn btn-outline-secondary" onClick={onClear}>
          <i className="bi bi-x-circle me-1"></i>Clear All Filters
        </button>
        <button className="btn btn-primary" onClick={onApply}>
          <i className="bi bi-search me-1"></i>Search / Apply Filters
        </button>
      </div>
    }
  >
    {children}
  </Drawer>
);

export default Drawer;
